<template>
  <v-navigation-drawer
    v-model='visible'
    absolute
    temporary
    right
    class='app-settings'
    >
    <v-list>
      <v-list-item>
        <v-skeleton-loader
          type='heading'
          v-if='!wz.length'
          />
        <v-select
          :value='this.region'
          @input='UpdateRegion'
          label='Region'
          :items='regions'
          v-else
          />
      </v-list-item>
      <v-list-item>
        <v-skeleton-loader
          type='heading'
          v-if='!wz.length'
          />
        <v-select
          :value='this.version'
          @input='version => ApplySetting({version})'
          label='Version'
          :items='versions'
          v-else
          />
      </v-list-item>

      <v-list-item>
        <v-btn @click='InstallNotifications' block color='primary'>Get notifications</v-btn>
      </v-list-item>

      <v-list-item class='color-picker-container'>
        <v-subheader>Background Color</v-subheader>
        <v-color-picker
          elevation="4"
          v-model='backgroundColor'
          />
      </v-list-item>

      <v-list-item>
        <v-select v-model='appFont' :items='supportedFonts' label='Font' />
      </v-list-item>

      <v-list-item>
        <v-switch
          v-model='showActiveDownloads'
          label='Show Download Activity'
          />
      </v-list-item>

      <v-list-item>
        <v-switch
          v-model='showSimGrid'
          label='Show Grid'
          />
      </v-list-item>

      <v-list-item>
        <v-slider
          min='0'
          max='100'
          step="1"
          v-model='simGridOpacity'
          label='Grid Opacity'
          thumb-label
          />
      </v-list-item>

      <v-list-item>
        <v-switch
          v-model='darkMode'
          label='Dark Mode'
          />
      </v-list-item>

      <v-list-item class='sha1'>
        Build: {{ SHA1 }}
      </v-list-item>
      <v-list-item class='sha1'>
        Branch name: {{ branchName }}
      </v-list-item>
      <v-list-item class='sha1'>
        Version: {{ appVersion }}
      </v-list-item>
      <v-list-item class='sha1'>
        <a href="https://npmjs.com/package/maplestory" target="_blank">maplestory.js</a>: {{ MapleStoryJSVersion }}
      </v-list-item>
      <v-list-item class='service-worker-status' v-if='development'>
        <strong>Service Worker Status:</strong>
        <div>
          <div
            v-for='status in FriendlyServiceWorkerStatus'
            :key='status.label'
          >
            <strong>{{status.label}}</strong>: <span>{{status.value}}</span>
          </div>
        </div>
      </v-list-item>
      <v-list-item class='pwa-status' v-if='development'>
        <strong>PWA Status:</strong>
        <div>
          <div
            v-for='status in FriendlyPWAStatus'
            :key='status.label'
          >
            <strong>{{status.label}}</strong>: <span>{{status.value}}</span>
          </div>
        </div>
      </v-list-item>
      <v-list-item>
        <v-switch
          v-model='isDevMode'
          label='Developer Mode'
          />
      </v-list-item>
      <v-list-item>
        <v-switch
          v-model='isClientSideRender'
          label='Client side rendering'
          />
      </v-list-item>
      <v-list-item v-if='PWA.BeforeInstallPrompt'>
        <v-btn @click='e => PWA.BeforeInstallPrompt.prompt()' block color='primary'>Install</v-btn>
      </v-list-item>
      <v-list-item v-if='ServiceWorkerStatus.Registered'>
        <v-btn @click='Update' block color='secondary'>Update</v-btn>
      </v-list-item>
      <v-list-item>
        <v-btn :href='ReloadTo' block color='secondary'>Reload</v-btn>
      </v-list-item>
      <v-list-item>
        <v-btn @click='LoadWZData' block color='secondary'>Re-fetch WZ Data</v-btn>
      </v-list-item>
      <v-list-item v-if='isDevMode'>
        <v-btn @click='Wipe' block color='red'>Clear all data</v-btn>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import API from '../API'
import DrawerNames from '@/Constants/Drawers'

export default {
  name: 'App.Settings',

  data: () => ({
    wz: [],
    wzLookup: {},
    SHA1: process.env.VUE_APP_GIT_HASH || 'Development',
    appVersion: process.env.VUE_APP_VERSION || 'Development',
    MapleStoryJSVersion: process.env.VUE_APP_MAPLESTORY_JS_VERSION || 'Development',
    branchName: process.env.VUE_APP_BRANCH_NAME,
    isStableBranch: process.env.VUE_APP_BRANCH_NAME === 'master',
    supportedFonts: ['QuanPixel8px', 'Roboto', 'sans-serif', 'Comic Sans MS']
  }),

  created() {
    this.LoadWZData()
  },

  methods: {
    ...mapActions(['OpenDrawer', 'ApplySetting']),

    UpdateRegion(region) {
      const versions = this.GetVersionsFor(region)
      const version = versions[0]

      this.ApplySetting({ region, version })
    },

    GetVersionsFor(region) {
      return this.wz.filter(wz => wz.region === region)
        .map(wz => wz.mapleVersionId)
        .reverse()
    },

    Update() {
      this.ServiceWorkerStatus.Registered.update()
    },

    LoadWZData() {
      API.WZ.List()
      .then(wz => {
        this.wz = wz
        this.wzLookup = wz.reduce((total, current) => {
          if (!total[current.region])
            total[current.region] = {}

          total[current.region][current.mapleVersionId] = current
          return total
        }, {})
      })
    },

    Wipe() {
      window.StateSynchronizer.Wipe()
    },

    InstallNotifications() {
      window.OneSignal.showSlidedownPrompt()
    }
  },

  computed: {
    ...mapState(['drawerOpen', 'region', 'version', 'background', 'development', 'clientSideRender', 'ServiceWorkerStatus', 'PWA', 'font', 'showDownloads', 'isDarkMode', 'gridOpacity', 'showGrid']),

    showSimGrid: {
      get() { return this.showGrid },
      set(showGrid) {
        this.ApplySetting({showGrid})
      }
    },

    simGridOpacity: {
      get() { return this.gridOpacity * 100 },
      set(gridOpacity) {
        this.ApplySetting({gridOpacity: gridOpacity / 100})
      }
    },

    darkMode: {
      get () { return this.isDarkMode },
      set(isDarkMode) {
        this.ApplySetting({isDarkMode})
      }
    },

    showActiveDownloads: {
      get() { return this.showDownloads },
      set(showDownloads) { this.ApplySetting({showDownloads}) }
    },

    appFont: {
      get() { return this.font },
      set(font) {
        this.ApplySetting({font})
      }
    },

    visible: {
      get() { return this.drawerOpen === DrawerNames.AppSettings },
      set(value) {
        if (value && !this.visible)
          this.OpenDrawer(DrawerNames.AppSettings)
        else if (!value && this.visible)
          this.OpenDrawer(null)
      }
    },

    regions() { return Object.keys(this.wzLookup) },
    versions() { return this.GetVersionsFor(this.region) },

    isDevMode: {
      get(){ return this.development },
      set(development) { this.ApplySetting({development}) }
    },

    isClientSideRender: {
      get(){ return this.clientSideRender },
      set(clientSideRender) { this.ApplySetting({clientSideRender}) }
    },

    backgroundColor: {
      get() { return this.background },
      set(background) { this.ApplySetting({ background }) }
    },

    FriendlyServiceWorkerStatus() {
      if (!this.development) return []

      return Object.keys(this.ServiceWorkerStatus).reduce((total, current) => {
        total.push({
          label: current,
          value: JSON.stringify(this.ServiceWorkerStatus[current])
        })

        return total
      }, [])
    },

    FriendlyPWAStatus() {
      if (!this.development) return []

      return Object.keys(this.PWA).reduce((total, current) => {
        total.push({
          label: current,
          value: JSON.stringify(this.PWA[current])
        })

        return total
      }, [])
    },

    ReloadTo() {
      return `${window.location.origin}/?t=${Date.now()}`
    }
  }
}
</script>

<style lang="scss">
.app-settings {
  min-width: 350px;

  .v-navigation-drawer__content {
    padding: 0 0 16px 0;

    .v-skeleton-loader {
      width: 100%;

      .v-skeleton-loader__heading {
        width: 100%;
      }
    }
  }

  .color-picker-container {
    flex-direction: column;
    align-items: flex-start;

    .v-color-picker {
      align-self: center;
    }
  }

  .sha1 {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    justify-content: flex-start;
  }

  .pwa-status {
    strong {
      min-width: 155px;
    }
  }

  .service-worker-status {
    strong {
      min-width: 100px;
    }
  }

  .service-worker-status, .pwa-status {
    justify-content: flex-start;
    flex-direction: column;
    align-items: baseline;

    strong {
      display: inline-block;
    }

    span {
      margin-left: 4px;
    }
  }
}
</style>